import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Dropdown,
  DropdownDivider,
  DropdownGroup,
  DropdownItem,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  openDialog,
  showNotification,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {filter} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetServiceItemMoveReasonsQuery,
  usePatchServiceItemMoveReasonSetInactiveMutation,
  usePostServiceItemMoveReasonMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {MoveReasonDefaultForm} from './MoveReasonDefaultForm';

interface MoveReasonTemplatesProps extends RequiredTestIdProps {
  reason: string;
  onPrefill: (reason: string) => void;
  isDisabled?: boolean;
}

const deleteTemplateSchema = z.object({
  codelistId: z.string().min(1),
});

type DeleteTemplateFormValues = z.infer<typeof deleteTemplateSchema>;

export function MoveReasonTemplates(props: MoveReasonTemplatesProps) {
  const {data: moveReasons} = useGetServiceItemMoveReasonsQuery();
  const [postMoveReason] = usePostServiceItemMoveReasonMutation();
  const [setMoveReasonInactive, {isLoading: isDeleteLoading}] =
    usePatchServiceItemMoveReasonSetInactiveMutation();

  // The codelist has no DELETE endpoint: a deleted template is deactivated and hidden here.
  const activeMoveReasons = filter((moveReason) => moveReason.isActive, moveReasons ?? []);

  const handleSaveAsTemplate = () =>
    postMoveReason({body: {name: props.reason.trim()}})
      .unwrap()
      .then(() => showNotification.success())
      .catch(handleApiError);

  const handleDelete: FormSubmitHandler<DeleteTemplateFormValues> = (values) =>
    setMoveReasonInactive({codelistId: values.codelistId})
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const handleDeleteClick = () =>
    openDialog(
      <Form<DeleteTemplateFormValues>
        experimentalZodSchema={deleteTemplateSchema}
        onSubmit={handleDelete}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="codelistId"
              type="choice"
              // The default template cannot be deleted, so it is listed but disabled.
              options={activeMoveReasons.map((moveReason) => ({
                value: moveReason.id,
                label: moveReason.isDefault
                  ? `${moveReason.name} (${i18n.t('general.labels.default')})`
                  : moveReason.name,
                isDisabled: moveReason.isDefault,
              }))}
              label={i18n.t('entity.vehicle.labels.template')}
              isRequired
              data-testid={suffixTestId('template', props)}
            />
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.cancel')}
                variant="secondary"
                isDisabled={isDeleteLoading}
                onClick={closeCurrentDialog}
                data-testid={suffixTestId('cancel', props)}
              />
              <FormButton
                control={control}
                title={i18n.t('general.actions.delete')}
                variant="danger"
                type="submit"
                data-testid={suffixTestId('delete', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>,
      {
        title: i18n.t('entity.vehicle.labels.deleteTemplate'),
        size: 'small',
        scrollBehavior: 'outside',
      }
    );

  const handleDefaultClick = () =>
    openDialog(
      <MoveReasonDefaultForm
        moveReasons={activeMoveReasons}
        data-testid={suffixTestId('defaultTemplate', props)}
      />,
      {
        title: i18n.t('entity.gdpr.labels.defaultTemplate'),
        size: 'small',
        scrollBehavior: 'outside',
      }
    );

  return (
    <Dropdown
      dropdownControl={
        <Button
          title={i18n.t('entity.vehicle.labels.templates')}
          variant="link"
          leftIcon="content/add_circle"
          size="small"
          isDisabled={props.isDisabled}
          data-testid={suffixTestId('templates', props)}
        />
      }
      data-testid={suffixTestId('templatesDropdown', props)}
    >
      <DropdownGroup>
        <DropdownItem
          label={i18n.t('entity.vehicle.labels.saveAsTemplate')}
          leftIcon="content/save"
          isDisabled={isNilOrEmpty(props.reason.trim())}
          onClick={handleSaveAsTemplate}
          data-testid={suffixTestId('saveAsTemplate', props)}
        />
        <Show when={isNotNilOrEmpty(activeMoveReasons)}>
          <DropdownItem
            label={i18n.t('entity.gdpr.labels.defaultTemplate')}
            leftIcon="toggle/star"
            onClick={handleDefaultClick}
            data-testid={suffixTestId('defaultTemplate', props)}
          />
        </Show>
        <Show when={activeMoveReasons.some((moveReason) => !moveReason.isDefault)}>
          <DropdownItem
            label={i18n.t('entity.vehicle.labels.deleteTemplate')}
            leftIcon="action/delete"
            severity="danger"
            onClick={handleDeleteClick}
            data-testid={suffixTestId('deleteTemplate', props)}
          />
        </Show>
      </DropdownGroup>
      <Show when={isNotNilOrEmpty(activeMoveReasons)}>
        <DropdownDivider />
        <DropdownGroup label={i18n.t('entity.vehicle.labels.fillFromTemplate')}>
          {activeMoveReasons.map((moveReason, index) => (
            <DropdownItem
              key={moveReason.id}
              label={moveReason.name}
              onClick={() => props.onPrefill(moveReason.name)}
              data-testid={suffixTestId(`template-[${index}]`, props)}
            />
          ))}
        </DropdownGroup>
      </Show>
    </Dropdown>
  );
}
